import { difference, uniq } from 'es-toolkit';
import React from 'react';
import {
  FilteringState,
  IntegratedFiltering,
  IntegratedSelection,
  IntegratedSorting,
  SelectionState,
  Sorting,
  SortingState,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  TableColumnResizing,
  TableColumnVisibility,
  TableFilterRow,
  TableHeaderRow,
  TableSelection,
  Toolbar,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import { Paper, TableCell } from '@mui/material';
import { castDraft } from 'immer';

import { expandToFillParentStyles } from '@paragon/client-tools';
import { getRanges, naturalSort, notNullish } from '@paragon/utilities';

import { HeightCache, WidthCache } from '../../../ComponentGeometryCaches';
import { getDistanceFormatterSelector } from '../../../selectors/getDistanceFormatterSelector';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabByAnalysisSetGuid } from '../../../selectors/productionComponentInstances';
import { getProductionGroupGridRows, ProductionGroupGridRow } from '../../../selectors/productionGroupGrid';
import { checkRows, setUserColumnWidths, uncheckRows } from '../../../store/batchesAndBundlesTabSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ComponentPreviewCell } from '../ComponentPreviewCell';
import { compareDistanceStrings, lengthFilter, trimmedCaseInsensitiveFilter } from '../gridUtilities';
import { ComponentsToBatchOrBundleGridRowActions } from './ComponentsToBatchOrBundleGridRowActions';
import { ComponentsToBatchOrBundleGridToolbarPlugin } from './ComponentsToBatchOrBundleGridToolbarPlugin';

interface Column {
  readonly name: string;
  readonly title: string;
  readonly width: number;
  readonly getCellValue?: (row: ProductionGroupGridRow) => null | string | number | React.JSX.Element;
}

// This sorting presently cannot be changed by the user. Creating bundles in the
// `createOrAddToBundleFromQuantitiesByAnalysisSetGuid` action will follow the same ordering, so if this changes, that
// needs to change as well. This lock-in was primarily motivated by the complexity of supporting multiple orderings and
// lack of perspective on what other component manufacturers would be looking for.
const sorting: Sorting[] = [
  { columnName: 'batch', direction: 'asc' },
  { columnName: 'width', direction: 'desc' },
  { columnName: 'name', direction: 'asc' },
];

const columnSorting = [
  { columnName: 'name', compare: naturalSort },
  { columnName: 'quantity', sortingEnabled: false },
  { columnName: 'plies', sortingEnabled: false },
  { columnName: 'thickness', sortingEnabled: false },
  { columnName: 'height', compare: compareDistanceStrings },
  { columnName: 'width', compare: compareDistanceStrings },
  { columnName: 'preview', sortingEnabled: false },
  { columnName: 'batch', compare: naturalSort },
];

const columnFiltering = [
  { columnName: 'name', predicate: trimmedCaseInsensitiveFilter, filteringEnabled: true },
  { columnName: 'quantity', predicate: trimmedCaseInsensitiveFilter, filteringEnabled: true },
  { columnName: 'plies', predicate: trimmedCaseInsensitiveFilter, filteringEnabled: true },
  { columnName: 'thickness', predicate: lengthFilter, filteringEnabled: true },
  { columnName: 'height', predicate: lengthFilter, filteringEnabled: true },
  { columnName: 'width', predicate: lengthFilter, filteringEnabled: true },
  { columnName: 'preview', filteringEnabled: false },
  { columnName: 'actions', filteringEnabled: false },
];
const columnsWithoutFilters = columnFiltering
  .filter((column) => !column.filteringEnabled)
  .map((column) => column.columnName);

const GridRoot = (props: Grid.RootProps) => (
  <Grid.Root {...props} sx={{ ...expandToFillParentStyles.targetStyleProps }} />
);

export function ComponentsToBatchOrBundleGrid() {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(getProductionGroupGridRows);
  const activeProjectGuid = useAppSelector((state) => state.batchesAndBundlesTab.activeProjectGuid);
  const activeProductionGroupGuid = useAppSelector((state) => state.batchesAndBundlesTab.activeProductionGroupGuid);
  const checkedRows = useAppSelector((state) => state.batchesAndBundlesTab.checkedAnalysisSetGuids);
  const batches = useAppSelector((state) => state.batches);
  const productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid = useAppSelector(
    getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabByAnalysisSetGuid,
  );
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);
  const activeList = useAppSelector((state) => state.batchesAndBundlesTab.activeList);
  const userColumnWidths = useAppSelector((state) => state.batchesAndBundlesTab.userColumnWidths);

  const columns: Column[] = [
    {
      name: 'name',
      title: 'Name',
      width: 200,
      getCellValue: (row) => row.truss?.name ?? 'Loading...',
    },
    {
      name: 'batch',
      title: 'Batch',
      width: 100,
      getCellValue: (row) => {
        const { analysisSetGuid } = row;
        const productionComponentInstancesForAnalysisSetGuid =
          productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid[analysisSetGuid];
        const batchIds = uniq(
          productionComponentInstancesForAnalysisSetGuid
            .map((productionComponentInstance) => productionComponentInstance.batchGuid)
            .filter(notNullish),
        ).map((batchGuid) => batches[batchGuid].id);
        return batchIds.length === 0 ? null : getRanges(batchIds).join(', ');
      },
    },
    {
      name: 'quantity',
      title: 'Quantity',
      width: 100,
    },
    {
      name: 'plies',
      title: 'Plies',
      width: 75,
      getCellValue: (row) => {
        const { truss } = row;
        if (!truss) return null;
        return truss.numberOfPlies === 1 ? null : truss.numberOfPlies;
      },
    },
    {
      name: 'thickness',
      title: 'Thickness',
      width: 125,
      getCellValue: (row) => {
        const { truss } = row;
        if (!truss) return null;
        return distanceFormatter(truss.thickness);
      },
    },
    {
      name: 'height',
      title: 'Height',
      width: 100,
      getCellValue: (row) => {
        const { truss } = row;
        if (!truss) return 'Loading';
        const height = HeightCache.get(row.analysisSetGuid, truss);
        return distanceFormatter(height);
      },
    },
    {
      name: 'width',
      title: 'Width',
      width: 100,
      getCellValue: (row) => {
        const { truss } = row;
        if (!truss) return 'Loading';
        const width = WidthCache.get(row.analysisSetGuid, truss);
        return distanceFormatter(width);
      },
    },
    {
      name: 'preview',
      title: 'Preview',
      width: 100,
      getCellValue: (row) => (
        <ComponentPreviewCell analysisSetGuid={row.analysisSetGuid} maxWidth={100} maxHeight={25} />
      ),
    },
    {
      name: 'actions',
      title: 'Actions',
      width: 100,
      getCellValue: (row) => <ComponentsToBatchOrBundleGridRowActions row={row} />,
    },
  ];

  function onCheckedRowsChange(newCheckedRows: (string | number)[]) {
    dispatch(uncheckRows(difference(checkedRows, newCheckedRows).map((rowToUncheck) => rowToUncheck.toString())));
    const newCheckedRowsStrings = difference(newCheckedRows, checkedRows).map((rowToCheck) => rowToCheck.toString());
    dispatch(checkRows(newCheckedRowsStrings));
  }

  return (
    <Paper sx={{ flex: 2, ...expandToFillParentStyles.containerStyleProps }}>
      <Grid rows={rows} columns={columns} getRowId={(row) => row.analysisSetGuid} rootComponent={GridRoot}>
        <SortingState sorting={sorting} />
        <IntegratedSorting columnExtensions={columnSorting} />

        {/* It is important that this is defined before <SelectionState /> and <IntegratedSelection /> so that select
        all takes into account filtering.*/}
        <FilteringState columnExtensions={columnFiltering} />
        <IntegratedFiltering columnExtensions={columnFiltering} />

        <SelectionState selection={castDraft(checkedRows)} onSelectionChange={onCheckedRowsChange} />
        <IntegratedSelection />

        <VirtualTable
          messages={{
            noData:
              activeProjectGuid === null
                ? 'Select a project'
                : activeProductionGroupGuid === null
                  ? 'Select a production group'
                  : activeList === 'batches'
                    ? 'Fully batched!'
                    : 'Fully bundled!',
          }}
        />
        <TableColumnVisibility />
        <Toolbar />
        <ComponentsToBatchOrBundleGridToolbarPlugin />
        <TableColumnResizing
          columnWidths={userColumnWidths ?? columns.map((column) => ({ columnName: column.name, width: column.width }))}
          onColumnWidthsChange={(newUserColumnWidths) => void dispatch(setUserColumnWidths(newUserColumnWidths))}
        />
        <TableHeaderRow />
        <TableFilterRow
          cellComponent={(props) =>
            columnsWithoutFilters.includes(props.column.name) ? <TableCell /> : <TableFilterRow.Cell {...props} />
          }
        />
        <TableSelection selectByRowClick showSelectAll />
      </Grid>
    </Paper>
  );
}
